import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import { isItem } from '@mimir/common/dto/searchResultHit';
import type { FolderViewModel } from '@mimir/common/dto/folderModels';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import { canMoveItems, canUpdateFolder } from '@mimir/common/util/security';
import { isFolderUpdatableByUser } from '@mimir/common/util/itemAccessControl';
import type UserDto from '@mimir/common/dto/userDto';
import type { TimedMetadataItem } from '@mimir/common/dto/timedMetadataModels';
import type { BookmarkTreeNode } from '@mimir/common/bookmark/bookmarks';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { GraphicsPayloadModel } from '@mimir/common/dto/mdfModels';

export type DroppableItem = ClientItem | FolderViewModel | TimedMetadataItem | BookmarkTreeNode | DroppableMetaGraphicsTemplate;
export interface DroppableMetaGraphicsTemplate {
  id: string,
  itemType: 'graphics',
  graphicsPayloadModel: GraphicsPayloadModel,
  durationInSeconds: Fraction,
  minimumDurationInSeconds: Fraction | null,
}

export const getNonFoldersCount = (selectedItems: DroppableItem[]) => {
  let itemCount = 0;
  for (const item of selectedItems) {
    if (isItem(item) && item.itemType !== ItemType.FOLDER) {
      ++itemCount;
    }
  }
  return itemCount;
};

/**
 * @returns An object containing two lists: movable items and non-movable items.
 * Note: the items can be folder and non-folder item types.
 */
export const getMovableAndNonMovableItems = (
  selectedItems: DroppableItem[],
  user: UserDto & { tenantId: string },
) => {
  /*
    Filter out the items that are not movable by the user.
    If the item is a folder, then check if the folder is updatable by the user.
    If the item is not a folder, then check if the user has the permission to move items.
  */
  const movableItems = selectedItems.filter(isItem).filter(item => {
    if (item.itemType !== ItemType.FOLDER) {
      return canMoveItems(user.permissions);
    }

    return canUpdateFolder(user.permissions) && isFolderUpdatableByUser(item.updatableBy, item.owner, undefined, user);
  });

  const movableItemIds: ReadonlySet<string> = new Set(movableItems.map(item => item.id));
  const nonMovableItems = selectedItems.filter(item => isItem(item) && !movableItemIds.has(item.id)).filter(isItem);

  return { movableItems, nonMovableItems };
};

/**
 * @returns a SafeHtmlBuilder with the base html for the dialog that asks the user
 * if they are sure they want to move the selected items. Showing the number of folders
 * and items that will be moved/copied. Also, if the user does not have permission to
 * move items, a message will be shown. And if the user tries to move a folder that is
 * not updatable by them, a message will be shown as a list.
 */
export const getBaseHtmlForItemsDropped = (params: {
  operationName: string,
  droppedItems: DroppableItem[],
  pathOfDestinationFolder: string,
  user: UserDto & { tenantId: string },
}): SafeHtmlBuilder => {
  const { operationName, droppedItems, pathOfDestinationFolder, user } = params;

  // Separate the items that can be moved/copied and the items that cannot be moved/copied.
  const { movableItems, nonMovableItems } = getMovableAndNonMovableItems(droppedItems, user);

  const movableNonFolderItemCount = getNonFoldersCount(movableItems);
  const movableFolderCount = movableItems.length - movableNonFolderItemCount;

  const baseHtml = new SafeHtmlBuilder();
  {
    /*
      If only one folder is selected: "Are you sure you want to move 'folderName' to 'targetFolderPath'?"
      Other Cases: "Are you sure you want to move X folder(s) and (copy) Y item(s) to 'targetFolderPath'?"
      If there is nothing to move/copy: "There is nothing to move/copy to 'targetFolderPath'."
    */
    baseHtml.addText(movableItems.length ? 'Are you sure you want to ' : 'There is nothing to move/copy ');

    if (movableFolderCount === 1 && movableNonFolderItemCount === 0) {
      const folderName = movableItems[0]!.name ?? '';
      baseHtml.addText(`${operationName.toLowerCase()} `).addBoldText(`${folderName} `);
    } else {
      /*
        If there are multiple folders and items selected, then
        add text how many folders and items are being moved/copied.
        for example: "move X folder(s) and (copy) Y item(s) to 'targetFolderPath'?"
      */
      const includeFolderText = movableFolderCount > 0;
      const includeItemText = movableNonFolderItemCount > 0;
      if (includeFolderText) {
        baseHtml.addText('move ').addBoldText(`${movableFolderCount}`).addText(` folder${movableFolderCount > 1 ? 's' : ''} `);
      }
      if (includeFolderText && includeItemText) {
        baseHtml.addText('and ');
      }

      if (includeItemText) {
        baseHtml
          .addText(operationName === 'Move/Copy' ? 'copy ' : '')
          .addBoldText(`${movableNonFolderItemCount}`)
          .addText(` item${movableNonFolderItemCount > 1 ? 's' : ''} `);
      }
    }

    baseHtml.addText('to: ').addBoldText(pathOfDestinationFolder).addText(movableItems.length ? ' ?' : '.');
  }

  if (movableFolderCount >= 1) {
    // add a note that the folders will not inherit the visibility of the destination folder.
    baseHtml
      .addLineBreaks(2)
      .addBoldText('Note:')
      .addText(` the folder${movableFolderCount > 1 ? 's' : ''} being moved and ${movableFolderCount > 1 ? 'their' : 'its'} contents will not inherit destination folder `)
      .addBoldText(pathOfDestinationFolder)
      .addText('\'s visibility.');
  }

  const nonMovableNonFolderItemCount = getNonFoldersCount(nonMovableItems);
  if (nonMovableNonFolderItemCount > 0) {
    // add a message that the items will not be copied/moved and the user does not have permission to move items.
    baseHtml
      .addLineBreaks(2)
      .addText('Selected ')
      .addBoldText(`${nonMovableNonFolderItemCount}`)
      .addText(` item${nonMovableNonFolderItemCount > 1 ? 's' : ''} will not ${operationName === 'Move/Copy' ? 'copy' : operationName.toLocaleLowerCase()}. `)
      .addBoldText('You do not have permission to add or move items to folders.');
  }

  const nonMovableFolders = nonMovableItems.filter(item => item.itemType === ItemType.FOLDER);
  const nonMovableFolderCount = nonMovableFolders.length;

  if (nonMovableFolderCount) {
    if (!canUpdateFolder(user.permissions)) {
      /*
        If the user does not have permission to move folders, then add a message that
        the folders will not be moved and the user does not have permission to move folders
      */
      baseHtml
        .addLineBreaks(2)
        .addText('Selected ')
        .addBoldText(`${nonMovableFolderCount}`)
        .addText(` folder${nonMovableFolderCount > 1 ? 's' : ''} will not move. `)
        .addBoldText(`You do not have permission to update the folder${nonMovableFolderCount > 1 ? 's' : ''}.`);
    } else {
      /*
        If the user has permission to move folders but the folders are not updatable by the user,
        then add a message listing the folders that will not be moved.
      */
      baseHtml
        .addLineBreaks(2)
        .addText('The following ')
        .addBoldText(`${nonMovableFolderCount}`)
        .addText(` selected folder${nonMovableFolderCount > 1 ? 's' : ''} will not move. (not updatable by the user):`)
        .addUnorderedListWithBoldText(nonMovableFolders.map(folder => folder.name ?? ''));
    }
  }

  return baseHtml;
};
